import { DiscoveryTable } from '@/components/discovery/DiscoveryTable';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { DiscoveryStatus } from '@/lib/discoveries';
import { Network, Plus } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

type TabKey = 'all' | 'active' | 'completed' | 'failed';

// Backend now accepts a comma-list on ``?status=`` → ``IN (...)``. We use it
// for the "Active" tab (queued + running) so newly-submitted runs show up
// before the worker has picked them up. The other tabs stay single-value
// for now — surfacing "Failed" alongside timeouts/cancels is a UX decision
// that hasn't been made yet.
function statusFor(tab: TabKey): DiscoveryStatus | readonly DiscoveryStatus[] | undefined {
  if (tab === 'all') return undefined;
  if (tab === 'active') return ['queued', 'running'] as const;
  if (tab === 'completed') return 'succeeded';
  if (tab === 'failed') return 'failed';
  return undefined;
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center rounded-md border border-dashed py-16 text-center">
      <Network className="mb-3 h-10 w-10 text-muted-foreground" />
      <h3 className="text-base font-medium">No discoveries yet</h3>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">
        Pick a dataset and run your first causal-graph discovery.
      </p>
      <Button asChild className="mt-4">
        <Link to="/discoveries/new">
          <Plus className="mr-1 h-4 w-4" /> New discovery
        </Link>
      </Button>
    </div>
  );
}

export default function Discoveries() {
  const [tab, setTab] = React.useState<TabKey>('all');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Discoveries</h1>
          <p className="text-sm text-muted-foreground">All discovery runs in this workspace.</p>
        </div>
        <Button asChild>
          <Link to="/discoveries/new">
            <Plus className="mr-1 h-4 w-4" /> New discovery
          </Link>
        </Button>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
          <TabsTrigger value="failed">Failed</TabsTrigger>
        </TabsList>
      </Tabs>

      <DiscoveryTable
        status={statusFor(tab)}
        emptyState={<EmptyState />}
        // Poll on every tab, not just "active" — the hook only actually
        // refetches while at least one visible row is non-terminal and
        // self-stops once everything is terminal, so this is cheap and fixes
        // the "all" tab not updating until F5.
        enableActivePolling
      />
    </div>
  );
}
